import { useEffect, useRef, useState } from 'react';
import Phaser from 'phaser';
import { BootScene } from './game/scenes/BootScene.js';
import { ZoneScene } from './game/scenes/ZoneScene.js';
import { applyPixelFiltering } from './game/loaders/pixelFilter.js';
import { initGameBridge, destroyGameBridge } from './bridge/GameBridge.js';
import { initPixelCitizenTest } from './dev/pixelCitizenTest.js';
import { blockBrowserZoom, viewportSize } from './lib/viewport.js';
import { HUD } from './ui/components/HUD.js';
import { PlayerNameplates } from './ui/overlays/PlayerNameplates.js';
import { ChatBubbles } from './ui/overlays/ChatBubbles.js';
import { ChatInput } from './ui/overlays/ChatInput.js';
import { GameHUD, GameHUDToolbar } from './ui/overlays/GameHUD.js';
import { VideoTiles } from './ui/overlays/VideoTiles.js';
import { DoorPrompt } from './ui/overlays/DoorPrompt.js';
import { ScooterPrompt } from './ui/overlays/ScooterPrompt.js';
import { IframeOverlay } from './ui/overlays/IframeOverlay.js';
import { LaunchPrompt } from './ui/overlays/LaunchPrompt.js';
import { KnockNotification } from './ui/overlays/KnockNotification.js';
import { Toast } from './ui/overlays/Toast.js';
import { ConnectionBanner } from './ui/overlays/ConnectionBanner.js';
import { UpdateBanner } from './ui/overlays/UpdateBanner.js';
import { RoomClaimPrompt } from './ui/overlays/RoomClaimPrompt.js';
import { BoardSharePrompt } from './ui/overlays/BoardSharePrompt.js';
import { KnockPrompt } from './ui/overlays/KnockPrompt.js';
import { DeskPrompt } from './ui/overlays/DeskPrompt.js';
import { PersonalDeskPrompt } from './ui/overlays/PersonalDeskPrompt.js';
import { ElevatorPanel } from './ui/overlays/ElevatorPanel.js';
import { DeskCustomizePanel } from './ui/overlays/DeskCustomizePanel.js';
import { DeskTutorial } from './ui/overlays/DeskTutorial.js';
import { FeedbackNudge } from './ui/overlays/FeedbackNudge.js';
import { LiveSessions } from './ui/overlays/LiveSessions.js';
import { WhiteboardHost } from './ui/whiteboard/WhiteboardHost.js';
import { NoticeBoardModal } from './ui/whiteboard/NoticeBoardModal.js';
import { PodiumHUD } from './ui/overlays/PodiumHUD.js';
import { RecordingBanner } from './ui/overlays/RecordingBanner.js';
import { WaveNotifications } from './ui/overlays/WaveNotifications.js';
import { ProximityChatPanel } from './ui/overlays/ProximityChatPanel.js';
import { TapToMove } from './ui/controls/TapToMove.js';
import { LoginPage } from './ui/pages/LoginPage.js';
import { GuestPage } from './ui/pages/GuestPage.js';
import { SetPasswordPage } from './ui/pages/SetPasswordPage.js';
import { AvatarPage } from './ui/pages/AvatarPage.js';
import { LobbyPage } from './ui/pages/LobbyPage.js';
import { SessionReplacedPage } from './ui/pages/SessionReplacedPage.js';
import { EventBus, useGameStore } from '@campus/core';
import { AdminLayout } from './ui/admin/AdminLayout.js';


/**
 * GamePage — Canvas Phaser + overlays React.
 * Rendu uniquement quand authState === 'in-game'.
 *
 * RÈGLE : React n'entre jamais dans les Phaser scenes.
 * GameBridge est le seul point de couplage EventBus ↔ Zustand.
 *
 * Layout : le canvas couvre tout l'écran visible ; la barre de contrôle flotte
 * par-dessus la carte, en bas (design system Placio, comme Gather).
 */
function GamePage({ background }: { background: boolean }) {
  const rootRef   = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const gameRef   = useRef<Phaser.Game | null>(null);
  const activeModal    = useGameStore((s) => s.activeModal);
  const isTransitioning = useGameStore((s) => s.isTransitioning);

  // Hauteur réelle du viewport (visualViewport plutôt que 100vh) — sur mobile,
  // 100vh inclut la zone recouverte par la barre d'adresse du navigateur, ce qui
  // pousse la toolbar du bas hors de l'écran visible (et overflow:hidden empêche
  // de scroller pour la révéler). visualViewport reflète l'espace réellement affiché.
  const [viewportHeight, setViewportHeight] = useState(
    () => viewportSize().height,
  );

  useEffect(() => {
    const updateViewportHeight = () => {
      setViewportHeight(viewportSize().height);
    };
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);
    window.visualViewport?.addEventListener('resize', updateViewportHeight);
    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
      window.visualViewport?.removeEventListener('resize', updateViewportHeight);
    };
  }, []);

  useEffect(() => {
    if (!canvasRef.current || gameRef.current) return;

    initGameBridge();
    initPixelCitizenTest();

    const { width: initialWidth, height: initialHeight } = viewportSize();

    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      parent: canvasRef.current,
      width: initialWidth,
      height: initialHeight,
      backgroundColor: '#1a1a2e',
      scene: [BootScene, ZoneScene],
      physics: {
        default: 'arcade',
        // Pas variable : le déplacement suit le temps écoulé. En pas fixe (60/s),
        // l'avatar avançait d'un pas par image affichée quelle que soit sa durée :
        // saccades dès qu'une image traîne, et un pas sur deux à 120 Hz.
        arcade: { debug: false, fixedStep: false },
      },
      scale: {
        mode: Phaser.Scale.RESIZE,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
    };

    gameRef.current = new Phaser.Game(config);
    applyPixelFiltering(gameRef.current);

    // Empêcher le zoom navigateur (Ctrl+scroll / pincement, Safari compris)
    const unblockBrowserZoom = blockBrowserZoom();

    // Recalcule les dimensions du canvas au resize/rotation — corrige le mode RESIZE
    // qui ne réagit pas seul à l'apparition/disparition de la barre d'adresse mobile.
    const handleResize = () => {
      if (!gameRef.current) return;
      const { width, height } = viewportSize();
      gameRef.current.scale.resize(width, height);
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);

    return () => {
      unblockBrowserZoom();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
      destroyGameBridge();
      gameRef.current?.destroy(true);
      gameRef.current = null;
    };
  }, []);

  // Administration ouverte par-dessus : le campus reste connecté (Colyseus,
  // LiveKit — l'avatar reste dans sa bulle, la discussion continue) mais ne
  // dessine plus rien et rend le clavier à l'interface d'administration.
  useEffect(() => {
    // `inert` : React 18 ne connaît pas encore l'attribut.
    rootRef.current?.toggleAttribute('inert', background);
    const game = gameRef.current;
    if (!background || !game) return;
    EventBus.emit('ui:modal-opened', {});
    game.loop.sleep();
    // Phaser relance sa boucle quand l'onglet redevient visible.
    const keepAsleep = () => game.loop.sleep();
    game.events.on(Phaser.Core.Events.VISIBLE, keepAsleep);
    return () => {
      game.events.off(Phaser.Core.Events.VISIBLE, keepAsleep);
      game.loop.wake();
      EventBus.emit('ui:modal-closed', {});
    };
  }, [background]);

  return (
    <div
      ref={rootRef}
      aria-hidden={background || undefined}
      style={{
        display: 'flex', flexDirection: 'column', width: '100vw', height: viewportHeight, overflow: 'hidden',
        // Masqué sans changer de taille : le canvas Phaser (mode RESIZE) garde ses dimensions.
        ...(background && { position: 'fixed', inset: 0, visibility: 'hidden', pointerEvents: 'none' }),
      }}
    >
      {/* Zone de jeu (canvas + overlays) — prend tout l'espace restant */}
      <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
        {/* Canvas Phaser */}
        <div ref={canvasRef} style={{ position: 'absolute', inset: 0 }} />

        {/* Overlays React — superposés, ne bloquent pas les clics Phaser par défaut */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 10 }}>
          {/* Calque tap-to-move en tout premier : sans z-index positif, il reste au
              niveau "auto" et laisse tous les siblings rendus après lui (HUD, chat,
              modals) recevoir les taps sur leurs propres zones — voir le commentaire
              dans TapToMove.tsx pour le pourquoi. */}
          <TapToMove />
          <PlayerNameplates />
          <ChatBubbles />
          <HUD />
          <div style={{ pointerEvents: 'auto' }}>
            <ChatInput />
            <ProximityChatPanel />
          </div>

          {/* GameHUD sans la barre du bas (panneaux latéraux, modals, etc.) */}
          <GameHUD />
          <VideoTiles />
          <DoorPrompt />
          <ScooterPrompt />
          <IframeOverlay />
          {/* Piles : chaque élément prend sa place au lieu de recouvrir les autres (ds.css). */}
          <div className="pl-stack-bottom">
            <RecordingBanner />
            <LaunchPrompt />
            <KnockPrompt />
            <DeskPrompt />
            <PersonalDeskPrompt />
            <RoomClaimPrompt />
            <BoardSharePrompt />
            <ElevatorPanel />
          </div>
          <div className="pl-stack-tr">
            <LiveSessions />
            <KnockNotification />
            <PodiumHUD />
            <WaveNotifications />
            <FeedbackNudge />
          </div>
          <WhiteboardHost />
          <NoticeBoardModal />
          <Toast />
          <DeskCustomizePanel />
          <DeskTutorial />
          <GameHUDToolbar />
        </div>

        {/* Overlay de transition inter-zones */}
        {isTransitioning && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 50,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(26, 26, 46, 0.85)',
            color: '#ffffff', fontSize: '22px', fontFamily: 'sans-serif',
            pointerEvents: 'all',
          }}>
            Chargement…
          </div>
        )}

        {/* Modaux */}
        {activeModal === 'chat' && (
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'auto' }}>
            {/* <ChatModal /> */}
          </div>
        )}
        {activeModal === 'livekit' && (
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'auto' }}>
            {/* <LiveKitModal /> */}
          </div>
        )}
      </div>

    </div>
  );
}

/**
 * App — Routeur d'etat.
 *
 * Routing base sur authState (Zustand) — pas de React Router.
 *   'unauthenticated' → LoginPage (ou GuestPage si ?invite=, SetPasswordPage si ?setPassword=)
 *   'authenticated'   → AvatarPage
 *   'lobby'           → LobbyPage (choix micro/camera avant d'entrer, façon Zoom/Meet)
 *   'in-game'         → GamePage (canvas Phaser + tous les overlays)
 *   'replaced'        → SessionReplacedPage (campus ouvert dans une autre fenêtre)
 */
export function App() {
  const authState = useGameStore((s) => s.authState);
  const adminPage = useGameStore((s) => s.adminPage);
  const inZone = useGameStore((s) => s.currentZoneId !== null);

  if (authState === 'unauthenticated') {
    // Detect guest invite link in URL: /guest?invite={id} or ?invite={id}
    const params = new URLSearchParams(window.location.search);
    const inviteId = params.get('invite');
    if (inviteId) return <GuestPage inviteId={inviteId} />;
    // Lien de création/reset de mot de passe reçu par email : /#setPassword={token}
    // (fragment plutôt que query string — jamais envoyé au serveur, donc
    // jamais loggé par nginx, voir CLAUDE.md). Fallback ?setPassword= pour
    // les liens envoyés avant ce changement, encore valides (TTL jusqu'à 24h).
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const setPasswordToken = hashParams.get('setPassword') ?? params.get('setPassword');
    if (setPasswordToken) return <SetPasswordPage token={setPasswordToken} />;
    return <LoginPage />;
  }
  if (authState === 'authenticated') return <AvatarPage />;
  if (authState === 'lobby') return <LobbyPage />;
  if (authState === 'replaced') return <SessionReplacedPage />;
  // Admin : par-dessus le campus, qui reste monté (et connecté) en arrière-plan
  // pour que l'avatar garde sa place et sa bulle. Pas encore de carte ouverte
  // (ex. tenant sans carte, envoyé directement dans l'éditeur) : rien à garder,
  // le campus démarre au retour. Structure identique dans les deux cas, sinon
  // React remonterait GamePage (et relancerait le jeu) à chaque aller-retour.
  return (
    <>
      {(!adminPage || inZone) && <GamePage background={!!adminPage} />}
      {adminPage && <AdminLayout />}
      {/* Bandeaux en colonne en haut au centre : jamais l'un sur l'autre. */}
      <div className="pl-top-banners">
        <ConnectionBanner />
        <UpdateBanner />
      </div>
    </>
  );
}
